import React, { useState, useEffect } from 'react';
import { Text } from '@perch-labs/shrike';
import { percent } from '@perch-labs/warbler';
import { SAMPLE_ZIPCODE } from 'domain/vnm';
import { Card, Container } from 'styles';
import { copyFor } from 'config';
import { useEgridSubregion } from 'hooks/use-egrid-subregion.hook';
import {
  DataAndLabelHorizontalList,
  DataAndLabel,
} from 'components/data-and-label';
import { translateError } from 'utils/errors/translate-error';
import { SPONSORED_SOLAR } from 'pages/dashboard/dashboard.utils';
import {
  StyledInput,
  InputContainer,
  SearchIcon,
} from './energy-breakdown.style';
import { EnergyBreakdownChart } from './energy-breakdown-chart/energy-breakdown-chart.component';

const ZIP_CODE_LENGTH = 5;
const getCopy = copyFor('solar.energyBreakdown');

interface Small {
  margin: Margin;
}

interface Props {
  zipCode?: string;
  small?: Small;
  originationSourceType?: string;
}

export const EnergyBreakdown = ({
  zipCode,
  small,
  originationSourceType,
}: Props) => {
  const [zipCodeToSearch, setZipCodeToSearch] = useState(
    zipCode || SAMPLE_ZIPCODE
  );
  const [zipCodeInput, setZipCodeInput] = useState(zipCode || SAMPLE_ZIPCODE);
  const validZipCodeLength = zipCodeInput?.length === ZIP_CODE_LENGTH;

  const { data, error } = useEgridSubregion(zipCodeToSearch);

  useEffect(() => {
    if (validZipCodeLength) {
      setZipCodeToSearch(zipCodeInput);
    }
  }, [zipCodeInput]);

  const energySummary = data?.zipCode.egridSubregion?.energySummary;
  const energyBreakdown = data?.zipCode.egridSubregion?.energyBreakdown;
  const translatePercent = value => (value ? percent(value, 0) : '0%');
  const sponsoredSolar = originationSourceType === SPONSORED_SOLAR;

  const energySources = [
    {
      label: getCopy('stats.renewable'),
      value: translatePercent(energySummary?.renewables),
    },
    {
      label: getCopy('stats.nuclear'),
      value: translatePercent(energySummary?.nuclear),
    },
    {
      label: getCopy('stats.fossilFuel'),
      value: translatePercent(energySummary?.fossilFuels),
    },
  ];

  return (
    <Card margin={{ top: '24px' }} mobileVariant="flat" small={small}>
      <Card.Content>
        <Container flex alignItems="center">
          <Text
            tag="h2"
            textStyle="heading700"
            margin={{ bottom: '32px', right: '16px', top: '48px' }}
          >
            {getCopy('title')}
          </Text>
          <InputContainer>
            <StyledInput
              aria-label={getCopy('ariaLabel')}
              maxLength={ZIP_CODE_LENGTH}
              value={zipCodeInput}
              label=""
              name="zip code input"
              onChange={setZipCodeInput}
              errorText={translateError(error)}
            />
            <SearchIcon icon="UISearch" />
          </InputContainer>
        </Container>
        <Text margin={{ bottom: '40px' }} className="notranslate">
          {sponsoredSolar
            ? getCopy('sponsoredSolarSubTitle', { zipCode: zipCodeToSearch })
            : getCopy('subTitle', { zipCode: zipCodeToSearch })}
        </Text>
        <DataAndLabelHorizontalList>
          {energySources.map(energySource => (
            <DataAndLabel key={energySource.label} {...energySource} />
          ))}
        </DataAndLabelHorizontalList>
        {energyBreakdown && (
          <EnergyBreakdownChart energyBreakdown={energyBreakdown} />
        )}
      </Card.Content>
    </Card>
  );
};
